import { useCallback, useEffect, useRef, useState } from "react";

const useWebSocket = (url, socketTicket, getSocketTicketFn, onWebSocketOpen, onMessage) => {
  const webSocketInstance = useRef(null);
  const messageQueue = useRef([]);
  const [isSocketOpen, setIsSocketOpen] = useState(false);

  const resetWebSocket = useCallback(() => {
    if (webSocketInstance?.current?.readyState === 1 || webSocketInstance?.current?.readyState === 0) {
      webSocketInstance?.current?.close?.();
    }
    webSocketInstance.current = null;
    setIsSocketOpen(false);
  }, []);

  useEffect(() => {
    return () => {
      resetWebSocket();
    };
  }, []);

  useEffect(() => {
    if (socketTicket && url) {
      if (webSocketInstance.current) {
        resetWebSocket();
      }
      webSocketInstance.current = new WebSocket(`${url}/${socketTicket}`);
      webSocketInstance.current.onopen = (event) => {
        if (event?.type === "open" && webSocketInstance?.current?.readyState === 1) {
          if (onWebSocketOpen) {
            onWebSocketOpen?.(webSocketInstance.current);
          }
          setIsSocketOpen(true);
        }
      };
    }
  }, [socketTicket, url, resetWebSocket, onWebSocketOpen]);

  useEffect(() => {
    if (isSocketOpen) {
      webSocketInstance.current.onmessage = (event) => {
        onMessage(event);
      };
      webSocketInstance.current.onerror = () => {
        webSocketInstance.current = null;
        setIsSocketOpen(false);
        getSocketTicketFn();
      };
    }
  }, [onMessage, isSocketOpen, getSocketTicketFn]);

  const getWebSocket = useCallback(() => {
    return webSocketInstance?.current;
  }, []);

  const sendMessage = useCallback((message, keep = true) => {
    if (webSocketInstance.current?.readyState === 1) {
      webSocketInstance?.current?.send?.(message);
    } else if (keep) {
      messageQueue?.current?.push?.(message);
    }
  }, []);

  useEffect(() => {
    if (webSocketInstance?.current?.readyState === 1) {
      messageQueue?.current?.splice?.(0).forEach?.((message) => {
        sendMessage(message);
      });
    }
  }, [webSocketInstance?.current?.readyState, sendMessage]);

  useEffect(() => {
    return () => {
      webSocketInstance.current = null;
      messageQueue.current = null;
      setIsSocketOpen(false);
    };
  }, []);
  return { webSocketInstance: webSocketInstance?.current, isSocketOpen, getWebSocket: getWebSocket, sendMessage: sendMessage };
};
export default useWebSocket;
